import React, { useState, useEffect } from 'react';
import { Music, Eye, EyeOff, Waves, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface AuthProps {
  onBack?: () => void;
  selectedPackageId?: string | null;
}

const Auth: React.FC<AuthProps> = ({ onBack, selectedPackageId }) => {
  // Default to sign-up mode if a package is selected (free trial), otherwise sign-in mode
  const [isLogin, setIsLogin] = useState(!selectedPackageId);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<any>(null);

  const { signIn, signUp } = useAuth();

  useEffect(() => {
    if (selectedPackageId) {
      fetchPackageDetails();
    }
  }, [selectedPackageId]);

  const fetchPackageDetails = async () => {
    if (!selectedPackageId) return;

    // Guard against selectedPackageId being an object
    if (typeof selectedPackageId !== 'string') {
      console.error('selectedPackageId must be a string, received:', typeof selectedPackageId, selectedPackageId);
      return;
    }

    try{
      const { data, error } = await supabase
        .from('subscription_packages')
        .select('*')
        .eq('id', selectedPackageId)
        .maybeSingle();

      if (!error && data) {
        setSelectedPackage(data);
      }
    } catch (err) {
      console.error('Error fetching package:', err);
    }
  };


  const getErrorMessage = (error: any) => {
    const message = error.message || '';

    if (message.includes('Invalid login credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (message.includes('Email not confirmed')) {
      return 'Please check your email and confirm your account first.';
    }
    if (message.includes('User already registered')) {
      return 'An account with this email already exists. Please sign in instead.';
    }
    if (message.includes('Password should be at least')) {
      return 'Password should be at least 6 characters long.';
    }
    if (message.includes('Unable to validate email')) {
      return 'Please enter a valid email address.';
    }
    if (message.includes('too many requests')) {
      return 'Too many failed attempts. Please try again later.';
    }

    return error.message || 'Authentication failed. Please try again.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      const { error } = isLogin 
        ? await signIn(email, password)
        : await signUp(email, password, fullName);

      if (error) {
        setError(getErrorMessage(error));
      } else if (!isLogin) {
        setError('');
        // After successful signup, automatically sign in the user
        const { error: signInError } = await signIn(email, password);
        if (signInError) {
          setError('Account created but auto-login failed. Please sign in manually.');
          setIsLogin(true);
          setPassword('');
        }
        // If there's a selected package and user is signed in, they will be redirected by App.tsx
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-secondary-900 to-black flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary-500/8 to-primary-600/5 rounded-full blur-3xl"></div>
      </div>

      {/* Back to Home Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-6 left-6 z-10 flex items-center space-x-2 text-white/70 hover:text-white transition-colors group"
        >
          <div className="p-2 rounded-lg bg-white/5 border border-white/10 group-hover:bg-white/10 group-hover:border-white/20 transition-all">
            <Home className="h-5 w-5" />
          </div>
          <span className="hidden sm:inline font-medium">Back to Home</span>
        </button>
      )}

      <div className="max-w-md w-full space-y-8">
        <div className="text-center relative">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full blur-xl opacity-60 animate-pulse"></div>
            <div className="relative bg-gradient-to-r from-primary-500 to-primary-600 p-4 rounded-full">
              <Music className="h-16 w-16 text-black" />
            </div>
            <Waves className="absolute -bottom-2 -right-2 h-8 w-8 text-primary-400 animate-bounce" />
          </div>
          <h2 className="mt-8 text-4xl font-display font-bold text-white">
            Welcome to
          </h2>
          <h1 className="text-5xl font-display font-bold bg-gradient-to-r from-primary-500 to-primary-400 bg-clip-text text-transparent mb-2">
            PlexyMusic
          </h1>
          <p className="text-white/80 font-medium">
            Background Music Platform for Shops
          </p>
          <p className="mt-2 text-sm text-white/60">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        {selectedPackage && (
          <div className="glass-effect p-4 rounded-xl border border-primary-500/30 bg-primary-500/10">
            <div className="text-center">
              <p className="text-white/70 text-sm mb-1">Selected Package</p>
              <p className="text-white font-bold text-lg">{selectedPackage.name}</p>
              <p className="text-primary-400 text-sm">
                €{(selectedPackage.monthly_price_cents / 100).toFixed(2)}/month
              </p>
              <p className="text-white/60 text-xs mt-2">
                Complete registration to proceed to checkout
              </p>
            </div>
          </div>
        )}

        <div className="card p-8 relative">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              {!isLogin && (
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-white/90 mb-2">
                    Full Name
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="input-field block w-full px-4 py-3"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field block w-full px-4 py-3"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field block w-full px-4 py-3 pr-12"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/60 hover:text-primary-400 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className={`text-sm text-center p-3 rounded-lg backdrop-blur-sm ${
                error.includes('created successfully') 
                  ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-500/20 text-green-300'
                  : 'bg-gradient-to-r from-red-500/10 to-red-600/10 border border-red-500/20 text-red-300'
              }`}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex justify-center py-4 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setEmail('');
                  setPassword('');
                  setFullName('');
                }}
                className="text-primary-500 hover:text-primary-400 text-sm font-medium transition-colors"
              >
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Auth;